import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { statusColor } from "@/constants/status"
import { isArrayOfDates } from "@/lib/is-array"
import { ColumnDef } from "@tanstack/react-table"
import { format, isSameDay } from "date-fns"
import { Edit2Icon, MoreVerticalIcon } from "lucide-react"
import { CalendarEvent } from "@/rental/calendar/calendar-types"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header"

const getColumns = (onEditClick: (e: React.MouseEvent, row: CalendarEvent) => void): ColumnDef<CalendarEvent>[] => [
    {
        id: "select",
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected() ||
                    (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                aria-label="Select all"
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
            />
        ),
        enableSorting: false,
        enableHiding: false,
    },
    {
        accessorKey: "title",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Vehicle" />
        ),
        cell: ({ row }) => (
            <div className="capitalize overflow-hidden" title={row.original.title}>
                <p>{row.original.title}</p>
            </div>),
        filterFn: (row, _id, value) => {
            const title = row.getValue("title") as string;
            const customerName = row.getValue("customerName") as string;
            const pickup = row.getValue("pickup") as string;
            const status = row.getValue("status") as string;
            return title?.toLowerCase().includes(value.toLowerCase()) ||
                customerName?.toLowerCase().includes(value.toLowerCase()) ||
                pickup?.toLowerCase().includes(value.toLowerCase()) ||
                status?.toLowerCase().includes(value.toLowerCase());
        },
    },
    {
        accessorKey: "customerName",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Customer" />
        ),
        cell: ({ row }) => (
            <div className="capitalize overflow-hidden" title={row.original.customerName || row.original.customerEmail || row.original.customerPhone}>
                <p>{row.original.customerName || row.original.customerEmail || row.original.customerPhone}</p>
            </div>
        ),
    },
    {
        accessorKey: "pickup",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Pickup" />
        ),
        cell: ({ row }) => (
            <div className="capitalize overflow-hidden" title={row.original.pickup}>
                <p>{row.original.pickup}</p>
            </div>
        ),
    },
    {
        accessorKey: "start",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Booking Start" />
        ),
        cell: ({ row }) => (
            <div className="capitalize overflow-hidden" title={format(row.getValue("start"), "dd MMM y, HH:mm")}>
                <p>{format(row.getValue("start"), "dd MMM y, HH:mm")}</p>
            </div>
        ),
        filterFn: (row, id, value) => {
            const rowValue = row.getValue(id);
            if (value instanceof Date && rowValue instanceof Date) {
                return isSameDay(value, rowValue);
            }
            if (Array.isArray(value)) {
                if (isArrayOfDates(value) && rowValue instanceof Date) {
                    const sorted = value.sort((a, b) => a.getTime() - b.getTime());
                    // TODO: check length
                    return (
                        sorted[0]?.getTime() <= rowValue.getTime() &&
                        rowValue.getTime() <= sorted[1]?.getTime()
                    );
                }
            }
            return false;
        },
    },
    {
        accessorKey: "end",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Booking End" />
        ),
        cell: ({ row }) => (
            <div className="capitalize overflow-hidden" title={format(row.getValue("end"), "dd MMM y, HH:mm")}>
                <p>{format(row.getValue("end"), "dd MMM y, HH:mm")}</p>
            </div>
        )
    },
    {
        accessorKey: "status",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => {
            const value = row.getValue("status") as string | string[];
            if (Array.isArray(value)) {
                return (
                    <div className="flex flex-wrap gap-1">
                        {value.map((v) => (
                            <Badge key={v} className={statusColor[v].badge}>
                                {v}
                            </Badge>
                        ))}
                    </div>
                );
            }
            return <Badge className={statusColor[value].badge}>{value}</Badge>;
        },
        filterFn: (row, id, value) => {
            const array = row.getValue(id) as string[];
            if (typeof value === "string") return array.includes(value);
            // up to the user to define either `.some` or `.every`
            if (Array.isArray(value)) return value.some((i) => array.includes(i));
            return false;
        },
    },
    {
        id: "actions",
        cell: ({ row }) => (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                        size="icon"
                    >
                        <MoreVerticalIcon />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32 overflow-hidden">
                    <DropdownMenuItem onClick={(e) => onEditClick(e, row.original)} >
                        <Edit2Icon className="mr-2 h-4 w-4" />
                        Edit
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        ),
    }
];

export { getColumns };